import { useCallback, useMemo, useState } from 'react'
import useContentfulGlobal from '../../../../hooks/useContentfulGlobal'

type ValidationResult = {
  isValid: boolean
  error?: string
}

const useCustomizableInput = (initialValues: string[] = []) => {
  const { blacklistedWords } = useContentfulGlobal()

  const [values, setValues] = useState<string[]>(initialValues)
  const [errors, setErrors] = useState<{ [key: number]: string | undefined }>({})

  const blacklistedWordsSet = useMemo(() => new Set(blacklistedWords?.map((word) => word.toLowerCase()) || []), [
    blacklistedWords,
  ])

  const validateInput = useCallback(
    (value: string): ValidationResult => {
      if (!value || value.trim() === '') {
        return {
          isValid: true,
        }
      }

      if (value.length > 9) {
        return {
          isValid: false,
          error: 'Máximo 9 caracteres',
        }
      }
      const letterAndSpaceRegex = /^[A-Za-z\sÁÉÍÓÚáéíóú]+$/
      if (!letterAndSpaceRegex.test(value)) {
        return {
          isValid: false,
          error: 'Solo se permiten letras y espacios',
        }
      }

      const lowerCaseValue = value.toLowerCase()
      const words = lowerCaseValue.split(' ')
      const isBlacklisted = words.some((word) => blacklistedWordsSet.has(word))
      if (isBlacklisted) {
        return {
          isValid: false,
          error: 'Nombre no permitido',
        }
      }

      return {
        isValid: true,
      }
    },
    [blacklistedWordsSet],
  )

  const updateValue = useCallback(
    (index: number, value: string) => {
      const validation = validateInput(value)
      setValues((prev) => {
        const newValues = [...prev]
        newValues[index] = value
        return newValues
      })

      setErrors((prev) => ({
        ...prev,
        [index]: validation.isValid ? undefined : validation.error,
      }))
      return validation
    },
    [validateInput],
  )

  const validateAll = useCallback((): boolean => {
    const newErrors: { [key: number]: string } = {}
    let allValid = true

    values.forEach((value, index) => {
      if (value && value.trim() !== '') {
        const validation = validateInput(value)
        if (!validation.isValid) {
          newErrors[index] = validation.error || ''
          allValid = false
        }
      }
    })

    setErrors(newErrors)
    return allValid
  }, [values, validateInput])

  const validateSingle = useCallback(
    (index: number): boolean => {
      const value = values[index]
      const validation = validateInput(value)

      setErrors((prev) => ({
        ...prev,
        [index]: validation.isValid ? undefined : validation.error,
      }))

      return validation.isValid
    },
    [values, validateInput],
  )

  const resetValues = useCallback((newValues: string[]) => {
    setValues(newValues)
    setErrors({})
  }, [])

  return {
    errors,
    resetValues,
    setValues,
    setErrors,
    updateValue,
    validateAll,
    validateSingle,
    values,
  }
}

export default useCustomizableInput
